<template>
	<!-- Tema propio "kpis": filtros y charts en la misma familia violeta
		(los demás tokens caen a los defaults de :root, idénticos a los del
		tema ventas). -->
	<div class="kpis_tab report_theme_container" data-theme="kpis">
		<TabHeader
			title="KPIs"
			description="Indicadores de tu estado de resultados"
			:filterPeriod="filterPeriod"
			:filterOptions="filterOptions"
			:endMonth="endMonth"
			:getNow="getNow"
			calendarMode="range"
			:dateRange="calendarRange"
			:year-range="yearRange"
			@update-filter-period="updateFilterPeriod"
			@update-end-month="updateEndMonth"
			@update-date-range="updateDateRange"
			@update-year-range="updateYearRange">
			<!-- Filtro central del tab (spec "Graficos KPIs Resultados"): cada
				grupo muestra sus 4 gráficos. Mismo formato que los sub-filters
				de Ventas (Cuentas/Categorías/Clientes). -->
			<template #center-sub-nav>
				<div class="sub_nav_filters">
					<button v-for="group in kpiGroups" :key="group.id"
						type="button"
						class="filter_btn"
						:class="{ 'is-active': activeSubFilter === group.id }"
						@click="updateActiveSubFilter(group.id)">
						{{ group.label }}
					</button>
				</div>
			</template>
		</TabHeader>

		<!-- Grilla 2×2: los 4 gráficos del grupo activo. Ventas y Personas
			están cableados; Márgenes/Terceros llegan por fases → placeholders. -->
		<div class="kpi_grid mt-2">
			<template v-if="activeGroupImplemented">
				<article v-for="card in activeCards" :key="card.id" class="kpi_card">
					<header class="kpi_card_header">
						<span class="kpi_card_title">{{ card.title }}</span>
						<!-- Switch del card (Boton YoY/Recur/$ de la spec): las dos
							lentes visibles (ej. MoM % | YoY %); se presiona una u
							otra y el thumb se desliza a la activa. Sin refetch. -->
						<div v-if="card.toggle"
							class="kpi_switch"
							role="group"
							:aria-label="'Vista de ' + card.title">
							<span class="kpi_switch_thumb"
								:class="{ 'is-right': toggles[card.id] }"
								aria-hidden="true"></span>
							<button type="button"
								class="kpi_switch_option"
								:class="{ 'is-active': !toggles[card.id] }"
								:aria-pressed="!toggles[card.id] ? 'true' : 'false'"
								@click="toggles[card.id] = false">
								{{ card.toggle[0] }}
							</button>
							<button type="button"
								class="kpi_switch_option"
								:class="{ 'is-active': toggles[card.id] }"
								:aria-pressed="toggles[card.id] ? 'true' : 'false'"
								@click="toggles[card.id] = true">
								{{ card.toggle[1] }}
							</button>
						</div>
					</header>
					<div class="kpi_card_body">
						<p v-if="error" class="kpi_state kpi_state--error">{{ error }}</p>
						<p v-else-if="isLoading" class="kpi_state">Cargando…</p>
						<p v-else-if="!hasKpisData" class="kpi_state">
							Sin datos para el período seleccionado.
						</p>
						<KpiComposedChart v-else
							:monthHeaders="card.monthHeaders || kpisData.monthHeaders"
							:series="card.series" />
					</div>
				</article>
			</template>
			<template v-else>
				<article v-for="n in 4" :key="n" class="kpi_card kpi_card--placeholder">
					<div class="kpi_state">Próximamente</div>
				</article>
			</template>
		</div>
	</div>
</template>

<script>
import moment from "moment";
import "moment/locale/es";
import { mapState, mapActions, mapMutations } from "vuex";
import TabHeader from "@/components/financialReports/TabHeader.vue";
import KpiComposedChart from "@/components/financialReports/charts/KpiComposedChart.vue";
import { FILTER_PERIOD_OPTIONS } from "@/config/reportStructure";
import { defaultYearRange } from "@/store/financialReports/utils/dateRange";

// Grupos del filtro central (spec de la hoja "Graficos KPIs Resultados").
// Sólo "ventas" tiene backend hoy; el resto muestra placeholders y no fetchea.
const KPI_GROUPS = [
	{ id: "ventas", label: "Ventas", implemented: true },
	{ id: "margenes", label: "Márgenes", implemented: true },
	{ id: "personas", label: "Personas", implemented: true },
	{ id: "terceros", label: "Terceros", implemented: true },
];

// Multi-línea desde pares [nombre, serie]; omite las series null (no aplican
// al super tipo de la empresa, ej. margen producto en Servicios).
function lineSeries(entries, unit) {
	return entries
		.filter(([, data]) => data != null)
		.map(([name, data]) => ({ name, type: "line", unit, data }));
}

// Series por equipo desde el dict del backend ({ equipo: [valores] }).
function teamSeries(porEquipo, { type, unit, stack = null }) {
	return Object.entries(porEquipo || {}).map(([name, data]) => ({
		name, type, unit, data, ...(stack ? { stack } : {}),
	}));
}

export default {
	name: "KpisTab",

	components: {
		TabHeader,
		KpiComposedChart,
	},

	data() {
		return {
			filterOptions: FILTER_PERIOD_OPTIONS,
			kpiGroups: KPI_GROUPS,
			activeSubFilter: "ventas",
			// Toggle activo de cada card (false = variante base). Declarados
			// upfront — sin Vue.set (regla de migración).
			toggles: {
				g1: false, g2: false, g3: false, g4: false,
				g5: false, g6: false, g7: false, g8: false,
			},
		};
	},

	computed: {
		...mapState("kpisReport", ["kpisData", "isLoading", "error"]),
		// Filtros de tiempo COMPARTIDOS con el resto de los tabs del reporte
		// (mismo wiring que VentasTab): granularidad en `financialReports`,
		// mes/rangos en `reportsFilters` — cambiar de tab conserva el período
		// y el rango activos.
		...mapState("reportsFilters", [
			"endMonth", "selectedPeriodRange", "customRange", "customYearRange",
		]),
		...mapState("financialReports", ["filterPeriod"]),

		getNow() {
			return moment().toDate();
		},

		// Rango activo del calendar del header. Si hay `customRange` lo usamos
		// tal cual; si no, derivamos el último año a partir de endMonth para que
		// el calendar abra pintando un rango razonable en lugar de "—".
		calendarRange() {
			if (this.customRange) return this.customRange;
			const end = moment(this.endMonth).startOf("month");
			return { from: end.clone().subtract(11, "months").toDate(), to: end.toDate() };
		},

		// Rango de años del picker de Anual: el custom elegido, o el default de
		// Anual (mismo `defaultYearRange` que usa el fetch) para que el trigger
		// abra pintando el span real y nunca difiera de los gráficos.
		yearRange() {
			return this.customYearRange || defaultYearRange(this.endMonth);
		},

		hasKpisData() {
			return Boolean(
				this.kpisData
				&& this.kpisData.group === this.activeSubFilter
				&& this.kpisData.monthHeaders.length > 0,
			);
		},

		// Cards del grupo activo (cada grupo implementado arma sus 4 configs).
		activeCards() {
			if (this.activeSubFilter === "personas") return this.personasCards;
			if (this.activeSubFilter === "terceros") return this.tercerosCards;
			if (this.activeSubFilter === "margenes") return this.margenesCards;
			return this.ventasCards;
		},

		// Configs de los 4 cards del grupo Ventas (Gráficos 1-4 de la spec).
		// Cada switch alterna series ya presentes en la respuesta — instantáneo.
		// `toggle` = par [lente base, lente alterna] del switch segmentado;
		// null/undefined = sin switch.
		ventasCards() {
			// El crecimiento período-a-período sigue la granularidad activa
			// (MoM en mensual, QoQ en trimestral). En YTD/Anual el bucket
			// previo ES el año anterior → el switch YoY se vuelve redundante
			// y se oculta.
			const popShort = this.filterPeriod === "quarter" ? "QoQ %" : "MoM %";
			const yoyToggleApplies = ["month", "quarter"].includes(this.filterPeriod);
			const g1Toggle = yoyToggleApplies ? [popShort, "YoY %"] : null;
			if (!this.hasKpisData) {
				// Sin data igual se renderizan los 4 cards (con su estado
				// vacío/cargando); las series no se leen en ese caso.
				return [
					{ id: "g1", title: "Ventas y crecimiento", toggle: g1Toggle },
					{ id: "g2", title: "Clientes y ticket promedio", toggle: ["Totales", "Recurrentes"] },
					{ id: "g3", title: "Clientes nuevos y salientes", toggle: ["N°", "Monto $"] },
					{ id: "g4", title: "Flujo de clientes sobre el total", toggle: ["N°", "Monto $"] },
				];
			}
			const { ventasCrecimiento, clientesTicket, flujoClientes, flujoRatios } = this.kpisData;
			const popLabel = this.filterPeriod === "quarter"
				? "Crecimiento QoQ %"
				: (this.filterPeriod === "month" ? "Crecimiento MoM %" : "Crecimiento YoY %");
			return [
				{
					id: "g1",
					title: "Ventas y crecimiento",
					toggle: g1Toggle,
					series: [
						{ name: "Ventas", type: "bar", unit: "money", data: ventasCrecimiento.ventas },
						this.toggles.g1 && yoyToggleApplies
							? { name: "Crecimiento YoY %", type: "line", unit: "percent", data: ventasCrecimiento.yoyPct }
							: { name: popLabel, type: "line", unit: "percent", data: ventasCrecimiento.momPct },
					],
				},
				{
					id: "g2",
					title: "Clientes y ticket promedio",
					toggle: ["Totales", "Recurrentes"],
					series: this.toggles.g2
						? [
							{ name: "Clientes Recurrentes", type: "bar", unit: "count", data: clientesTicket.recurrentes },
							{ name: "Ticket Promedio Recurrente ($)", type: "line", unit: "money", data: clientesTicket.arrpa },
						]
						: [
							{ name: "Clientes Totales", type: "bar", unit: "count", data: clientesTicket.clientes },
							{ name: "Ticket Promedio ($)", type: "line", unit: "money", data: clientesTicket.arpa },
						],
				},
				{
					id: "g3",
					title: "Clientes nuevos y salientes",
					toggle: ["N°", "Monto $"],
					// G3/G4 excluyen el mes actual: headers de la ventana
					// clampeada al mes anterior (flujo_period_labels).
					monthHeaders: this.kpisData.flujoMonthHeaders,
					series: this.toggles.g3
						? [
							{ name: "$ Nuevos Clientes", type: "bar", unit: "money", data: flujoClientes.nuevosAmount },
							{ name: "$ Clientes Salientes", type: "bar", unit: "money", data: flujoClientes.salientesAmount, down: true },
						]
						: [
							{ name: "Nuevos Clientes", type: "bar", unit: "count", data: flujoClientes.nuevos },
							{ name: "Clientes Salientes", type: "bar", unit: "count", data: flujoClientes.salientes, down: true },
						],
				},
				{
					id: "g4",
					title: "Flujo de clientes sobre el total",
					toggle: ["N°", "Monto $"],
					monthHeaders: this.kpisData.flujoMonthHeaders,
					// Solo barras (como G3): nuevos hacia arriba, salientes hacia
					// abajo — sin línea, ambas series son la misma unidad (%).
					series: this.toggles.g4
						? [
							{ name: "$ Nuevos / Ventas %", type: "bar", unit: "percent", data: flujoRatios.nuevosAmountPct },
							{ name: "$ Salientes / Ventas %", type: "bar", unit: "percent", data: flujoRatios.salientesAmountPct, down: true },
						]
						: [
							{ name: "Nuevos / Total %", type: "bar", unit: "percent", data: flujoRatios.nuevosPct },
							{ name: "Salientes / Total %", type: "bar", unit: "percent", data: flujoRatios.salientesPct, down: true },
						],
				},
			];
		},

		// Configs de los 4 cards del grupo Personas (Gráficos 5-8 de la spec).
		// El switch "Equipos" desglosa por las CUENTAS RRHH a las que están
		// prorrateados los docs de remuneración (backend: team_category es un
		// sentinel; las categorías no participan) — apiladas para $ y personas, multi-línea
		// para promedio y ratio.
		personasCards() {
			if (!this.hasKpisData) {
				return [
					{ id: "g5", title: "Remuneración total", toggle: ["Total", "Equipos"] },
					{ id: "g6", title: "Personas remuneradas", toggle: ["Total", "Equipos"] },
					{ id: "g7", title: "Remuneración promedio", toggle: ["Total", "Equipos"] },
					{ id: "g8", title: "Remuneraciones / Ventas", toggle: ["Total", "Equipos"] },
				];
			}
			const {
				remuneracionTotal, personas, remuneracionPromedio, remuneracionesVentas,
			} = this.kpisData;
			// Sin docs clasificados por cuenta no hay desglose → switch oculto.
			const hasTeams = Boolean(this.kpisData.teamCategory);
			const toggle = hasTeams ? ["Total", "Equipos"] : null;
			return [
				{
					id: "g5",
					title: "Remuneración total",
					toggle,
					series: this.toggles.g5 && hasTeams
						? teamSeries(remuneracionTotal.porEquipo, { type: "bar", unit: "money", stack: "equipos" })
						: [{ name: "Remuneración Total ($)", type: "bar", unit: "money", data: remuneracionTotal.total }],
				},
				{
					id: "g6",
					title: "Personas remuneradas",
					toggle,
					series: this.toggles.g6 && hasTeams
						? teamSeries(personas.porEquipo, { type: "bar", unit: "count", stack: "equipos" })
						: [{ name: "Personas", type: "bar", unit: "count", data: personas.total }],
				},
				{
					id: "g7",
					title: "Remuneración promedio",
					toggle,
					series: this.toggles.g7 && hasTeams
						? teamSeries(remuneracionPromedio.porEquipo, { type: "line", unit: "money" })
						: [{ name: "Remuneración Promedio ($)", type: "line", unit: "money", data: remuneracionPromedio.total }],
				},
				{
					id: "g8",
					title: "Remuneraciones / Ventas",
					toggle,
					series: this.toggles.g8 && hasTeams
						? teamSeries(remuneracionesVentas.porEquipoPct, { type: "line", unit: "percent" })
						: [{ name: "Remuneraciones / Ventas %", type: "line", unit: "percent", data: remuneracionesVentas.totalPct }],
				},
			];
		},

		// Configs de los 4 cards del grupo Terceros (Gráficos 9-12): proveedores
		// (facturas afectas/exentas excl. código 46) y honorarios. Sin toggles.
		tercerosCards() {
			if (!this.hasKpisData) {
				return [
					{ id: "g9", title: "Compras a proveedores" },
					{ id: "g10", title: "Proveedores y ticket promedio" },
					{ id: "g11", title: "Honorarios" },
					{ id: "g12", title: "Personas a honorarios y promedio" },
				];
			}
			const { proveedores, honorarios } = this.kpisData;
			return [
				{
					id: "g9",
					title: "Compras a proveedores",
					series: [
						{ name: "Proveedores ($)", type: "bar", unit: "money", data: proveedores.amount },
						{ name: "Proveedores / Ventas %", type: "line", unit: "percent", data: proveedores.ventasPct },
					],
				},
				{
					id: "g10",
					title: "Proveedores y ticket promedio",
					series: [
						{ name: "N° de Proveedores", type: "bar", unit: "count", data: proveedores.count },
						{ name: "Proveedor Promedio ($)", type: "line", unit: "money", data: proveedores.promedio },
					],
				},
				{
					id: "g11",
					title: "Honorarios",
					series: [
						{ name: "Honorarios ($)", type: "bar", unit: "money", data: honorarios.amount },
						{ name: "Honorarios / Ventas %", type: "line", unit: "percent", data: honorarios.ventasPct },
					],
				},
				{
					id: "g12",
					title: "Personas a honorarios y promedio",
					series: [
						{ name: "N° Personas Honorarios", type: "bar", unit: "count", data: honorarios.count },
						{ name: "Honorario Promedio ($)", type: "line", unit: "money", data: honorarios.promedio },
					],
				},
			];
		},

		// Configs de los 4 cards del grupo Márgenes (Gráficos 13-15 de la spec).
		// Sin toggles; las series que no aplican al super tipo se omiten.
		margenesCards() {
			if (!this.hasKpisData) {
				return [
					{ id: "g13", title: "Promedios por cliente" },
					{ id: "g14", title: "Márgenes %" },
					{ id: "g13b", title: "Promedios por persona" },
					{ id: "g15", title: "Gastos / Ventas %" },
				];
			}
			const {
				promedioCliente, margenesPct, promedioPersona, gastosVentasPct,
			} = this.kpisData;
			return [
				{
					id: "g13",
					title: "Promedios por cliente",
					series: lineSeries([
						["Ticket Promedio (ARPA)", promedioCliente.arpa],
						["Margen Bruto Promedio (AMPA)", promedioCliente.ampa],
						["Margen Contribución Promedio (ACMPA)", promedioCliente.acmpa],
						["EBITDA Promedio (AEPA)", promedioCliente.aepa],
					], "money"),
				},
				{
					id: "g14",
					title: "Márgenes %",
					series: lineSeries([
						["Margen Producto", margenesPct.producto],
						["Margen Bruto", margenesPct.bruto],
						["Margen Contribución", margenesPct.contribucion],
						["Margen EBITDA", margenesPct.ebitda],
						["Margen Neto", margenesPct.neto],
					], "percent"),
				},
				{
					id: "g13b",
					title: "Promedios por persona",
					series: lineSeries([
						["Venta por Persona", promedioPersona.venta],
						["Margen Bruto por Persona", promedioPersona.margenBruto],
						["Margen Contribución por Persona", promedioPersona.margenContribucion],
						["EBITDA por Persona", promedioPersona.ebitda],
					], "money"),
				},
				{
					id: "g15",
					title: "Gastos / Ventas %",
					series: lineSeries([
						["Totales / Ventas", gastosVentasPct.total],
						["Administración / Ventas", gastosVentasPct.administracion],
						["Comerciales / Ventas", gastosVentasPct.comerciales],
						["I+D / Ventas", gastosVentasPct.iD],
					], "percent"),
				},
			];
		},

		// Firma reactiva: un solo trigger de fetch aunque cambien varios filtros
		// en la misma transacción.
		fetchSignature() {
			return [
				this.endMonth,
				this.selectedPeriodRange,
				this.filterPeriod,
				this.activeSubFilter,
				this.customRange && this.customRange.from,
				this.customRange && this.customRange.to,
				this.customYearRange && this.customYearRange.from,
				this.customYearRange && this.customYearRange.to,
			].join("|");
		},

		activeGroupImplemented() {
			const group = this.kpiGroups.find((g) => g.id === this.activeSubFilter);
			return Boolean(group && group.implemented);
		},
	},

	methods: {
		...mapActions("kpisReport", ["fetchKpisResultados"]),
		...mapActions("reportsFilters", ["setCustomRange", "setCustomYearRange"]),
		...mapMutations("reportsFilters", ["SET_END_MONTH"]),
		...mapMutations("financialReports", ["SET_FILTER_PERIOD"]),

		loadCharts() {
			// Los grupos sin backend todavía no fetchean (placeholders).
			if (!this.activeGroupImplemented) return;
			this.fetchKpisResultados({
				endMonth: this.endMonth,
				selectedPeriodRange: this.selectedPeriodRange,
				customRange: this.customRange,
				customYearRange: this.customYearRange,
				period: this.filterPeriod,
				group: this.activeSubFilter,
			});
		},
		updateActiveSubFilter(group) {
			this.activeSubFilter = group;
		},
		updateEndMonth(date) {
			this.SET_END_MONTH(date);
		},
		// `setCustomRange` ya switchea selectedPeriodRange a "custom" y
		// sincroniza endMonth al `to` (y guarda vacíos), como en VentasTab.
		updateDateRange(range) {
			this.setCustomRange(range);
		},
		// Rango de años (Anual). Ortogonal al mes: la action no toca endMonth
		// ni selectedPeriodRange.
		updateYearRange(range) {
			this.setCustomYearRange(range);
		},
		updateFilterPeriod(period) {
			this.SET_FILTER_PERIOD(period);
		},
	},

	mounted() {
		this.loadCharts();
	},

	watch: {
		fetchSignature() {
			this.loadCharts();
		},
	},
};
</script>

<style scoped lang="scss">
.kpis_tab {
	position: relative;
	height: 100%;
}

.kpi_grid {
	display: grid;
	grid-template-columns: 1fr 1fr;
	grid-auto-rows: 340px;
	gap: 16px;
	width: 100%;

	@media (max-width: 1024px) {
		grid-template-columns: 1fr;
	}
}

.kpi_card {
	background: var(--report-bg);
	border: 1px solid var(--report-border);
	border-radius: 16px;
	box-shadow: var(--report-shadow-card);
	padding: 12px;
	display: flex;
	flex-direction: column;
	overflow: hidden;
}

.kpi_card_header {
	flex-shrink: 0;
	margin-bottom: 8px;
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 8px;
}

.kpi_card_title {
	color: var(--report-text);
	font-size: 14px;
	font-weight: 600;
}

// Switch del card (Boton YoY/Recur/$ de la spec): segmentado de dos lentes
// con thumb deslizante — la activa se lee sobre el primary del tema del tab,
// la otra queda visible como invitación. Mismo lenguaje que los sub-filters
// del header, en miniatura.
.kpi_switch {
	flex-shrink: 0;
	position: relative;
	display: inline-grid;
	grid-template-columns: 1fr 1fr;
	padding: 2px;
	border: 1px solid var(--report-border);
	border-radius: 999px;
	background: var(--report-surface);
}

.kpi_switch_thumb {
	position: absolute;
	top: 2px;
	bottom: 2px;
	left: 2px;
	width: calc(50% - 2px);
	border-radius: 999px;
	background: var(--report-filter-accent, var(--report-primary));
	transition: transform 0.18s ease;

	&.is-right {
		transform: translateX(100%);
	}

	@media (prefers-reduced-motion: reduce) {
		transition: none;
	}
}

.kpi_switch_option {
	position: relative;
	z-index: 1;
	padding: 3px 12px;
	border: none;
	border-radius: 999px;
	background: transparent;
	font-size: 12px;
	font-weight: 600;
	font-family: inherit;
	color: var(--report-text-muted-strong);
	cursor: pointer;
	white-space: nowrap;
	transition: color 0.18s ease;

	&:hover {
		color: var(--report-text);
	}

	&.is-active {
		color: var(--report-bg);
	}

	&:focus-visible {
		outline: 2px solid var(--report-filter-accent, var(--report-primary));
		outline-offset: 2px;
	}
}

.kpi_card_body {
	flex: 1;
	min-height: 0;
	position: relative;
	display: flex;
	align-items: stretch;
	justify-content: stretch;
}

.kpi_card--placeholder {
	align-items: center;
	justify-content: center;
	border-style: dashed;
}

.kpi_state {
	margin: auto;
	color: var(--report-text-muted);
	font-size: 13px;
	text-align: center;

	&--error {
		color: var(--report-negative);
	}
}

// Sub-filters del header inyectados por slot (los estilos scoped de TabHeader
// no alcanzan al markup del slot) — mismas classes que usa Ventas.
.sub_nav_filters {
	display: flex;
	background: var(--report-bg);
	border: 1px solid var(--report-border);
	border-radius: 8px;
	width: fit-content;
}

.filter_btn {
	padding: 6px 16px;
	border: none;
	background: transparent;
	border-radius: 6px;
	font-size: 14px;
	font-weight: 500;
	font-family: inherit;
	color: var(--report-text-muted-strong);
	cursor: pointer;
	transition: all 0.2s;

	&:hover {
		background: var(--report-filter-wash, var(--report-surface));
	}

	&.is-active {
		background: var(--report-filter-accent, var(--report-primary));
		color: var(--report-bg);

		&:hover {
			background: var(--report-filter-accent-hover, var(--report-primary-hover));
		}
	}
}
</style>
